import { useMemo, useState } from "react";
import { Box, Divider, IconButton, List, ListItemButton, ListItemText, Modal, Typography } from "@mui/material";
import { Close } from "@mui/icons-material";
import { helpDocs } from "../../datas/HelpDoc";

const modalBoxStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "85%",
  maxWidth: 1200,
  height: "85vh",
  display: "flex",
  flexDirection: "column",
  bgcolor: "var(--page-surface-bg)",
  border: "1px solid var(--page-surface-border)",
  borderRadius: 2,
  boxShadow: 24,
  overflow: "hidden",
} as const;

// helpDocs id 규칙: 최상위 = "메뉴", 하위 = "메뉴 > 세부메뉴"
const isSubId = (id: string) => id.includes(" > ");
const parentOf = (id: string) => id.split(" > ").slice(0, -1).join(" > ");

export default function ManualModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const topLevelDocs = useMemo(() => helpDocs.filter((d) => !isSubId(d.id)), []);
  const [selectedId, setSelectedId] = useState(topLevelDocs[0]?.id ?? "");

  const selectedDoc = helpDocs.find((d) => d.id === selectedId);
  const activeTopId = isSubId(selectedId) ? parentOf(selectedId) : selectedId;

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalBoxStyle}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
            borderBottom: "1px solid var(--page-surface-border)",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, color: "var(--page-title)" }}>
            데이터 분류분석 솔루션 매뉴얼
          </Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>

        <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
          <Box
            sx={{
              width: 220,
              flexShrink: 0,
              borderRight: "1px solid var(--page-surface-border)",
              overflowY: "auto",
              bgcolor: "var(--header-menu-bg)",
              p: 1,
            }}
          >
            <List dense>
              {topLevelDocs.map((doc) => {
                const subDocs = helpDocs.filter((d) => parentOf(d.id) === doc.id);
                const isActiveTop = activeTopId === doc.id;
                return (
                  <Box key={doc.id}>
                    <ListItemButton
                      selected={selectedId === doc.id}
                      onClick={() => setSelectedId(doc.id)}
                      sx={{ borderRadius: 1 }}
                    >
                      <ListItemText
                        primary={doc.title}
                        primaryTypographyProps={{ fontWeight: isActiveTop ? 700 : 500 }}
                      />
                    </ListItemButton>
                    {isActiveTop && subDocs.length > 0 && (
                      <Box sx={{ pl: 1.5 }}>
                        {subDocs.map((sub) => (
                          <ListItemButton
                            key={sub.id}
                            selected={selectedId === sub.id}
                            onClick={() => setSelectedId(sub.id)}
                            sx={{ borderRadius: 1, py: 0.25 }}
                          >
                            <ListItemText primary={sub.title} primaryTypographyProps={{ fontSize: 13 }} />
                          </ListItemButton>
                        ))}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </List>
          </Box>

          <Box sx={{ flex: 1, overflowY: "auto", p: 3 }}>
            {!selectedDoc ? (
              <Typography color="text.secondary">해당 페이지에 내용이 없습니다.</Typography>
            ) : (
              <>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: "var(--page-title)" }}>
                  {selectedDoc.title}
                </Typography>

                {selectedDoc.blocks.map((block, idx) => (
                  <Box key={idx} sx={{ mb: 3 }}>
                    {block.title && (
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                        {block.title}
                      </Typography>
                    )}
                    {block.body.map((line, li) => (
                      <Typography key={li} variant="body1" sx={{ mb: 0.5 }}>
                        {line}
                      </Typography>
                    ))}
                    {block.images && block.images.length > 0 && (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mt: 1.5 }}>
                        {block.images.map((src, ii) => (
                          <Box
                            key={ii}
                            component="img"
                            src={src}
                            alt={`${selectedDoc.title}-${idx}-${ii}`}
                            sx={{
                              maxWidth: block.images!.length > 1 ? "calc(50% - 8px)" : "100%",
                              width: "100%",
                              borderRadius: 1.5,
                              border: "1px solid var(--page-surface-border)",
                            }}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                ))}

                {selectedDoc.related && selectedDoc.related.length > 0 && (
                  <Box sx={{ mt: 4 }}>
                    <Divider sx={{ mb: 2 }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                      관련 항목
                    </Typography>
                    {selectedDoc.related.map((rel) => (
                      <Box
                        key={rel.id}
                        onClick={() => setSelectedId(rel.id)}
                        sx={{
                          p: 1.5,
                          mb: 1,
                          borderRadius: 1.5,
                          cursor: "pointer",
                          border: "1px solid var(--page-surface-border)",
                          "&:hover": { bgcolor: "var(--header-menu-item-hover)" },
                        }}
                      >
                        <Typography sx={{ fontWeight: 600 }}>{rel.title}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {rel.summary}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Modal>
  );
}
